import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Settings, Users, Truck, Check, Clock, AlertCircle, 
  ChevronRight, Package, Calendar, Bot, ArrowLeft, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import api from '@/utils/api';
import { toast } from 'sonner';

const TORTLAB_LOGO = "https://customer-assets.emergentagent.com/job_35f2eb62-f2f6-4b48-9388-e2c1ee261a61/artifacts/tfwz2zen_generated-image-1%20%284%29.png";

export default function AdminServiceQueue() {
  const navigate = useNavigate();
  const [serviceQueue, setServiceQueue] = useState({ setup_queue: [], ongoing_service_tenants: [] });
  const [allTenants, setAllTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showHardwareDialog, setShowHardwareDialog] = useState(false);
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [showServiceDialog, setShowServiceDialog] = useState(false);
  const [hardwareForm, setHardwareForm] = useState({ hardware_serial: '', hardware_status: '' });
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [queueRes, tenantsRes] = await Promise.all([
        api.get('/admin/service-queue'),
        api.get('/admin/tenants')
      ]);
      setServiceQueue(queueRes.data);
      setAllTenants(tenantsRes.data.tenants);
    } catch (error) {
      toast.error('Failed to fetch service data');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteSetup = async () => {
    if (!selectedTenant) return;
    setProcessing(true);
    try {
      await api.post(`/admin/tenants/${selectedTenant.tenant_id}/complete-setup`, {});
      toast.success('Setup marked as complete');
      setShowSetupDialog(false);
      setSelectedTenant(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete setup');
    } finally {
      setProcessing(false);
    }
  };

  const handleLogServiceReview = async () => {
    if (!selectedTenant) return;
    setProcessing(true);
    try {
      await api.post(`/admin/tenants/${selectedTenant.tenant_id}/service-review`, {});
      toast.success('Service review logged');
      setShowServiceDialog(false);
      setSelectedTenant(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to log service review');
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdateHardware = async () => {
    if (!selectedTenant) return;
    setProcessing(true);
    try {
      await api.put(`/admin/tenants/${selectedTenant.tenant_id}/hardware`, hardwareForm);
      toast.success('Hardware info updated');
      setShowHardwareDialog(false);
      setSelectedTenant(null);
      setHardwareForm({ hardware_serial: '', hardware_status: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update hardware');
    } finally {
      setProcessing(false);
    }
  };

  const openHardwareDialog = (tenant) => {
    setSelectedTenant(tenant);
    setHardwareForm({
      hardware_serial: tenant.hardware_serial || '',
      hardware_status: tenant.hardware_status || ''
    });
    setShowHardwareDialog(true);
  };

  const getHardwareStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 border-green-200';
      case 'shipped': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'pending': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'replacement_needed': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-service-queue">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200/50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} className="rounded-full">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <img 
                src={TORTLAB_LOGO} 
                alt="TortLab" 
                className="h-8 object-contain"
              />
              <div>
                <h1 className="text-xl font-heading font-bold text-foreground">Service Management</h1>
                <p className="text-sm text-muted-foreground">Platform Admin Dashboard</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={fetchData} className="rounded-full">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Setup Queue */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-heading font-bold flex items-center">
              <Settings className="w-5 h-5 mr-2 text-amber-500" />
              Setup Queue
              {serviceQueue.setup_queue.length > 0 && (
                <Badge className="ml-2 bg-amber-100 text-amber-800">{serviceQueue.setup_queue.length}</Badge>
              )}
            </h2>
          </div>

          {serviceQueue.setup_queue.length === 0 ? (
            <Card className="border-dashed border-2">
              <CardContent className="py-8 text-center">
                <Check className="w-12 h-12 text-green-500 mx-auto mb-2" />
                <p className="text-muted-foreground">No pending setups</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {serviceQueue.setup_queue.map((tenant) => (
                <Card key={tenant.tenant_id} className="border-2 border-amber-200 bg-amber-50/50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold">{tenant.name}</h3>
                        <p className="text-sm text-muted-foreground">
                          Subscribed: {tenant.subscription_started_at ? new Date(tenant.subscription_started_at).toLocaleDateString() : 'N/A'}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openHardwareDialog(tenant)}
                        >
                          <Truck className="w-4 h-4 mr-2" />
                          Hardware
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => {
                            setSelectedTenant(tenant);
                            setShowSetupDialog(true);
                          }}
                        >
                          <Check className="w-4 h-4 mr-2" />
                          Complete Setup
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>

        {/* Ongoing Service Tenants */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-heading font-bold flex items-center">
              <Users className="w-5 h-5 mr-2 text-blue-500" />
              Ongoing Service Accounts
              {serviceQueue.ongoing_service_tenants.length > 0 && (
                <Badge className="ml-2 bg-blue-100 text-blue-800">{serviceQueue.ongoing_service_tenants.length}</Badge>
              )}
            </h2>
          </div>

          {serviceQueue.ongoing_service_tenants.length === 0 ? (
            <Card className="border-dashed border-2">
              <CardContent className="py-8 text-center">
                <Package className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                <p className="text-muted-foreground">No accounts with ongoing service</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {serviceQueue.ongoing_service_tenants.map((tenant) => (
                <Card key={tenant.tenant_id} className="border-2">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center space-x-2">
                          <h3 className="font-semibold">{tenant.name}</h3>
                          <Badge variant="secondary" className="text-xs">
                            {tenant.pricing_plan?.name}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          Last review: {tenant.last_service_review_at ? new Date(tenant.last_service_review_at).toLocaleDateString() : 'Never'}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        {tenant.hardware_status && (
                          <Badge className={`border ${getHardwareStatusColor(tenant.hardware_status)}`}>
                            {tenant.hardware_status.replace('_', ' ')}
                          </Badge>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openHardwareDialog(tenant)}
                        >
                          <Truck className="w-4 h-4 mr-2" />
                          Hardware
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => {
                            setSelectedTenant(tenant);
                            setShowServiceDialog(true);
                          }}
                        >
                          <Calendar className="w-4 h-4 mr-2" />
                          Log Review
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>

        {/* All Tenants Overview */}
        <section>
          <h2 className="text-xl font-heading font-bold mb-4 flex items-center">
            <Bot className="w-5 h-5 mr-2 text-primary" />
            All Tenants
          </h2>

          <Card className="border-2">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left p-4 font-medium">Tenant</th>
                      <th className="text-left p-4 font-medium">Plan</th>
                      <th className="text-left p-4 font-medium">Status</th>
                      <th className="text-left p-4 font-medium">Hardware</th>
                      <th className="text-left p-4 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allTenants.map((tenant) => (
                      <tr key={tenant.tenant_id} className="border-b hover:bg-slate-50">
                        <td className="p-4">
                          <div className="font-medium">{tenant.name}</div>
                          <div className="text-xs text-muted-foreground">{tenant.tenant_id}</div>
                        </td>
                        <td className="p-4">
                          {tenant.pricing_plan ? (
                            <Badge variant="secondary">{tenant.pricing_plan.name}</Badge>
                          ) : (
                            <span className="text-muted-foreground">None</span>
                          )}
                        </td>
                        <td className="p-4">
                          <Badge className={`border ${
                            tenant.subscription_status === 'active' ? 'bg-green-100 text-green-800' :
                            tenant.subscription_status === 'trial' ? 'bg-blue-100 text-blue-800' :
                            'bg-slate-100 text-slate-800'
                          }`}>
                            {tenant.subscription_status}
                          </Badge>
                        </td>
                        <td className="p-4">
                          {tenant.hardware_status ? (
                            <div>
                              <Badge className={`border ${getHardwareStatusColor(tenant.hardware_status)}`}>
                                {tenant.hardware_status.replace('_', ' ')}
                              </Badge>
                              {tenant.hardware_serial && (
                                <div className="text-xs text-muted-foreground mt-1">
                                  S/N: {tenant.hardware_serial}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="p-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openHardwareDialog(tenant)}
                          >
                            <Settings className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </section>
      </main>

      {/* Complete Setup Dialog */}
      <Dialog open={showSetupDialog} onOpenChange={setShowSetupDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Complete Setup</DialogTitle>
            <DialogDescription>
              Mark the setup as complete for {selectedTenant?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              This will mark the initial setup as complete. The tenant will no longer appear in the setup queue.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSetupDialog(false)}>Cancel</Button>
            <Button onClick={handleCompleteSetup} disabled={processing}>
              {processing ? 'Processing...' : 'Complete Setup'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Log Service Review Dialog */}
      <Dialog open={showServiceDialog} onOpenChange={setShowServiceDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Log Service Review</DialogTitle>
            <DialogDescription>
              Record a service review for {selectedTenant?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              This will update the last service review date for tracking purposes.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowServiceDialog(false)}>Cancel</Button>
            <Button onClick={handleLogServiceReview} disabled={processing}>
              {processing ? 'Processing...' : 'Log Review'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hardware Update Dialog */}
      <Dialog open={showHardwareDialog} onOpenChange={setShowHardwareDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Hardware</DialogTitle>
            <DialogDescription>
              Manage hardware details for {selectedTenant?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="hardware_serial">Serial Number</Label>
              <Input
                id="hardware_serial"
                value={hardwareForm.hardware_serial}
                onChange={(e) => setHardwareForm({ ...hardwareForm, hardware_serial: e.target.value })}
                placeholder="Enter serial number"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="hardware_status">Status</Label>
              <Select
                value={hardwareForm.hardware_status}
                onValueChange={(value) => setHardwareForm({ ...hardwareForm, hardware_status: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="shipped">Shipped</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="replacement_needed">Replacement Needed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHardwareDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateHardware} disabled={processing}>
              {processing ? 'Processing...' : 'Update Hardware'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
